import { Button, Form, Input } from "antd";
import {
  ArrowRight,
  CircleCheck,
  Clock3,
  Mail,
  MapPin,
  MessageSquareText,
  Phone,
} from "lucide-react";
import { useState } from "react";
import PageHero from "../../components/PageHero";
import Faq from "../Home/components/Faq";
import { MAIL_API_URL } from "../../config/global.config";
import { COMPANY } from "../../constants/company.constants";
import WebLayout from "../../layouts/WebLayout";
import cn from "../../lib/cn";
import { toast } from "../../lib/toast";

const CONTACT_DETAILS = [
  {
    icon: Phone,
    label: "Phone",
    value: COMPANY.phone,
    href: `tel:${COMPANY.phone.replace(/\s/g, "")}`,
  },
  {
    icon: Mail,
    label: "Email",
    value: COMPANY.email,
    href: `mailto:${COMPANY.email}`,
  },
  { icon: MapPin, label: "Address", value: COMPANY.address },
  { icon: Clock3, label: "Business hours", value: COMPANY.supportHours },
];

const BENEFITS = [
  "Track attendance with geo-fenced, selfie check-ins",
  "Run payroll and payslips in a few clicks",
  "Hire faster with AI CV parsing and interviews",
  "Measure performance with KPIs and appraisals",
];

const panelClass = "rounded-2xl border border-gray-200 bg-gray-50/70";

const PanelTitle = ({ children }: { children: string }) => (
  <h2 className="border-b border-gray-200 pb-4 text-sm font-semibold uppercase tracking-wider text-gray-900">
    {children}
  </h2>
);

// Field names match the Upscale BD website so the mail service gets the same payload.
type FieldName =
  | "fName"
  | "lName"
  | "email"
  | "phoneNumber"
  | "companyName"
  | "message";

type FieldConfig = {
  name: FieldName;
  label: string;
  placeholder: string;
  type?: string;
  autoComplete?: string;
  required?: string; // error message when empty
  textarea?: boolean;
  wide?: boolean;
};

const FIELDS: FieldConfig[] = [
  {
    name: "companyName",
    label: "Company name",
    placeholder: "Enter your company name",
    autoComplete: "organization",
    required: "Please enter your company name",
    wide: true,
  },
  {
    name: "fName",
    label: "First name",
    placeholder: "Enter your first name",
    autoComplete: "given-name",
    required: "Please enter your first name",
  },
  {
    name: "lName",
    label: "Last name",
    placeholder: "Enter your last name",
    autoComplete: "family-name",
    required: "Please enter your last name",
  },
  {
    name: "email",
    label: "Work email",
    placeholder: "you@company.com",
    type: "email",
    autoComplete: "email",
    required: "Please enter your email",
  },
  {
    name: "phoneNumber",
    label: "Phone number",
    placeholder: "+8801XXXXXXXXX",
    type: "tel",
    autoComplete: "tel",
    required: "Please enter phone number",
  },
  {
    name: "message",
    label: "How can we help?",
    placeholder: "Tell us about your team and what you're looking for…",
    required: "Please enter message",
    textarea: true,
    wide: true,
  },
];

type ContactValues = Record<FieldName, string>;

const Contact = () => {
  const [form] = Form.useForm<ContactValues>();
  const [submitting, setSubmitting] = useState(false);

  const handleFinish = async (values: ContactValues) => {
    const payload = {
      ...Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          (value ?? "").trim(),
        ]),
      ),
      // The mail service requires a non-empty jobTitle (an empty string fails
      // with "Missing required fields"); HRx's form doesn't ask for it.
      jobTitle: "N/A",
    };

    try {
      setSubmitting(true);
      const response = await fetch(`${MAIL_API_URL}/mail/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("We couldn't send your message. Please try again.");
      }
      toast(
        "success",
        "Message sent successfully. Thank you for reaching out — we'll get back to you shortly.",
      );
      form.resetFields();
    } catch (error) {
      toast(
        "error",
        error instanceof Error ? error.message : "Something went wrong",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WebLayout>
      <PageHero
        badge={{ icon: MessageSquareText, label: "Contact" }}
        title="Get in touch"
        description="Have a question about HRx or want a demo for your team? Send us a message and our team will get back to you."
      />

      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-5 lg:grid-cols-[1.35fr_1fr]">
          {/* ---------- Form ---------- */}
          <Form
            form={form}
            layout="vertical"
            size='large'
            onFinish={handleFinish}
            className={cn(panelClass, "p-6 sm:p-8")}
          >
            <PanelTitle>Send us a message</PanelTitle>

            <div className="mt-6 grid gap-x-5 gap-y-5 sm:grid-cols-2">
              {FIELDS.map((field) => (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={
                    <span className="text-sm font-medium text-gray-700">
                      {field.label}
                    </span>
                  }
                  rules={[
                    ...(field.required
                      ? [
                          {
                            required: true,
                            whitespace: true,
                            message: field.required,
                          },
                        ]
                      : []),
                    ...(field.type === "email"
                      ? [
                          {
                            type: "email" as const,
                            message: "Please enter a valid email",
                          },
                        ]
                      : []),
                  ]}
                  className={cn("!mb-0", field.wide && "sm:col-span-2")}
                >
                  {field.textarea ? (
                    <Input.TextArea
                      rows={5}
                      placeholder={field.placeholder}
                      className="!resize-none rounded-lg"
                    />
                  ) : (
                    <Input
                      size="large"
                      type={field.type}
                      placeholder={field.placeholder}
                      autoComplete={field.autoComplete}
                      className="rounded-lg"
                    />
                  )}
                </Form.Item>
              ))}
            </div>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              block
              loading={submitting}
              className="btn-primary mt-7 h-11 font-medium"
            >
              {submitting ? "Sending…" : "Send message"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </Form>

          {/* ---------- Side panels ---------- */}
          <div>
            <div className="flex flex-col gap-5">
              <div className={cn(panelClass, "p-6 sm:p-7")}>
                <PanelTitle>With HRx you can</PanelTitle>
                <ul className="mt-5 space-y-3.5">
                  {BENEFITS.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex gap-3 text-sm leading-6 text-gray-700"
                    >
                      <CircleCheck
                        className="mt-[3px] h-4 w-4 shrink-0 text-theme-primary"
                        strokeWidth={2}
                      />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={cn(panelClass, "flex-1 p-6 sm:p-7")}>
                <PanelTitle>Contact information</PanelTitle>
                <dl className="mt-5 space-y-5">
                  {CONTACT_DETAILS.map(({ icon: Icon, label, value, href }) => (
                    <div key={label} className="flex gap-3">
                      <Icon
                        className="mt-0.5 h-[18px] w-[18px] shrink-0 text-theme-primary"
                        strokeWidth={1.75}
                      />
                      <div className="min-w-0">
                        <dt className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                          {label}
                        </dt>
                        <dd className="mt-1 text-sm leading-6 text-gray-700">
                          {href ? (
                            <a
                              href={href}
                              className="transition-colors hover:text-theme-primary"
                            >
                              {value}
                            </a>
                          ) : (
                            value
                          )}
                        </dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200">
        <Faq
          className="py-16 sm:py-20"
          containerClassName="max-w-[60rem] lg:gap-12"
          showContactCard={false}
        />
      </div>
    </WebLayout>
  );
};

export default Contact;
